import clsx from 'clsx';
import { InputHTMLAttributes, KeyboardEvent } from 'react';

import { FormError } from '../FormElements/FormElements';

import styles from './NumberInput.module.scss';

export interface NumberInputProps {
  value: number | undefined;
  onUpdate: (newValue: number | undefined) => void;
  min: number;
  max: number;
  placeholder: string;
  size: number;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { ref?: React.ForwardedRef<HTMLInputElement> };
  error?: string | React.ReactNode;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
function preventNonNumberInput(event: KeyboardEvent) {
  if (!event.key.match(/^[0-9]+$/) && event.key !== 'Backspace' && event.key !== 'Tab') {
    event.preventDefault();
  }

  // if user inputs number bigger then max value, set it to max (opposite for min value)
  const el = event.target as HTMLInputElement;
  if (el.value !== '') {
    if (parseInt(el.value, 10) < parseInt(el.min, 10)) {
      el.value = el.min;
    }
    if (parseInt(el.value, 10) > parseInt(el.max, 10)) {
      el.value = el.max;
    }
  }
}

export const NumberInput = ({ value, onUpdate, min, max, placeholder, size, inputProps }: NumberInputProps) => (
  <div className="flex rounded-md">
    <button
      onClick={() => onUpdate(clamp((value || 0) - 1, min, max))}
      className="p-1 border-2 border-gray-200 bg-gray-200 rounded-l-md"
      type="button"
    >
      -
    </button>
    <input
      {...inputProps}
      className="w-10 px-2 py-1 border-2 border-gray-200 text-center "
      type="number"
      id="name"
      min={min}
      max={max}
      size={size}
      placeholder={placeholder}
      value={value || ''}
      onChange={event => onUpdate(clamp(parseInt(event.target.value, 10), min, max))}
      onKeyDown={preventNonNumberInput}
    />
    <button
      onClick={() => onUpdate(clamp((value || 0) + 1, min, max))}
      className="p-1 border-2 border-gray-200 bg-gray-200 rounded-r-md"
      type="button"
    >
      +
    </button>
  </div>
);

export interface NumberInputWideProps {
  className?: string;
  error?: string | React.ReactNode;
  isCurrency?: boolean;
  isPercentage?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & { ref?: React.ForwardedRef<HTMLInputElement> };
}

export const NumberInputWide = ({ className, inputProps, error, isCurrency, isPercentage }: NumberInputWideProps) => (
  <div className={styles.inputWrapper}>
    <div style={{ position: 'relative' }}>
      <input
        {...inputProps}
        className={clsx(styles.numberInput, className, {
          [styles.hasError]: !!error,
          [styles.isCurrency]: isCurrency,
          [styles.isPercentage]: isPercentage,
        })}
        type="number"
        id="name"
        placeholder={inputProps.placeholder}
        onKeyUp={preventNonNumberInput}
      />
      {isCurrency && <span className={clsx(styles.symbol, styles.currency)}>€</span>}
      {isPercentage && <span className={clsx(styles.symbol, styles.percentage)}>%</span>}
    </div>
    {error && <FormError error={error} />}
  </div>
);
